import type { PipeDataStructure } from './types/pipe';

export type PipeEndType = 'EndA' | 'EndB';

/** Pipe status list entry or uploaded pipe data row. */
export type PipeEndDataEntry = {
  end?: string;
  pipe_end?: string;
  pipeEnd?: string;
  fileUrl?: string;
  file_url?: string;
  data?: PipeDataStructure | { pipeinfo?: { pipe_end?: string }; Data?: unknown; fileInfo?: unknown; projectInfo?: unknown };
  pipeinfo?: { pipe_end?: string };
};

export function parsePipeDataStructure(raw: unknown): PipeDataStructure | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested = record.data;
  const source =
    nested &&
    typeof nested === 'object' &&
    (nested as Record<string, unknown>).pipeinfo &&
    (nested as Record<string, unknown>).Data
      ? (nested as PipeDataStructure)
      : record.pipeinfo && record.Data
        ? (raw as PipeDataStructure)
        : null;

  if (!source?.pipeinfo || !source?.Data) return null;

  return {
    fileInfo: source.fileInfo ?? { Ver: '' },
    projectInfo: source.projectInfo ?? {
      project_num: '',
      project_name: '',
      customer_name: '',
      project_manager: '',
    },
    pipeinfo: source.pipeinfo,
    Data: source.Data,
  };
}

export function getPipeEndLabel(entry: PipeEndDataEntry | null | undefined): string {
  if (!entry) return '';
  return String(
    entry.end ??
      entry.pipe_end ??
      entry.pipeEnd ??
      entry.data?.pipeinfo?.pipe_end ??
      entry.pipeinfo?.pipe_end ??
      '',
  ).trim();
}

export function pipeEndMatches(entry: PipeEndDataEntry, endType: PipeEndType): boolean {
  const label = getPipeEndLabel(entry).replace(/\s/g, '');
  return label === endType;
}

export function findPipeEndEntry(
  pipeDataEntries: PipeEndDataEntry[] | null | undefined,
  endType: PipeEndType,
): PipeEndDataEntry | null {
  if (!Array.isArray(pipeDataEntries) || pipeDataEntries.length === 0) return null;

  const direct = pipeDataEntries.find((entry) => pipeEndMatches(entry, endType));
  if (direct) return direct;

  // Fallback: API order is often [EndA, EndB]
  if (endType === 'EndA' && pipeDataEntries.length >= 2) return pipeDataEntries[0];
  if (endType === 'EndB' && pipeDataEntries.length >= 1) {
    return pipeDataEntries.length >= 2 ? pipeDataEntries[1] : pipeDataEntries[0];
  }

  return null;
}

export function extractPipeDataFromMaterial(
  material: unknown,
  endType: PipeEndType,
): PipeDataStructure | null {
  const endData = (material as any)?.details?.pipeExtraDetails?.pipeData?.[endType];
  if (!endData) return null;
  return parsePipeDataStructure(endData.data ?? endData);
}

export function getPipeEndFileUrl(entry: PipeEndDataEntry | null | undefined): string | null {
  const url = entry?.fileUrl ?? entry?.file_url;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
}

export async function fetchPipeDataFromUrl(fileUrl: string): Promise<PipeDataStructure | null> {
  const response = await fetch(fileUrl);
  if (!response.ok) {
    throw new Error(`Failed to load pipe data (${response.status})`);
  }
  const json = await response.json();
  return parsePipeDataStructure(json);
}

/**
 * Load dimensional data for one pipe end: material details first, then pipe status fileUrl.
 */
export async function loadPipeDataForEnd(options: {
  material?: unknown;
  pipeDataEntries?: PipeEndDataEntry[] | null;
  endType: PipeEndType;
}): Promise<PipeDataStructure | null> {
  const { material, pipeDataEntries, endType } = options;

  const fromMaterial = extractPipeDataFromMaterial(material, endType);
  if (fromMaterial) return fromMaterial;

  const entry = findPipeEndEntry(pipeDataEntries, endType);
  if (!entry) return null;

  const embedded = parsePipeDataStructure(entry.data ?? entry);
  if (embedded) return embedded;

  const fileUrl = getPipeEndFileUrl(entry);
  if (!fileUrl) return null;

  return fetchPipeDataFromUrl(fileUrl);
}
